import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ConfirmationModal = ({
  isOpen,
  onClose,
  onConfirm,
  booking,
  modifications,
  updatedPricing,
  changeFees
}) => {
  if (!isOpen) return null;

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const formatCurrency = (amount) => {
    return `₱${amount?.toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  return (
    <div className="fixed inset-0 z-[1100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-card rounded-xl border border-border w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl">
        {/* Header */}
        <div className="bg-gradient-to-r from-primary to-secondary p-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-white mb-1">Confirm Modifications</h2>
              <p className="text-sm text-white/80">Review your changes before confirming</p>
            </div>
            <button
              onClick={onClose}
              className="w-10 h-10 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
              aria-label="Close"
            >
              <Icon name="X" size={20} className="text-white" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Original Booking Info */}
          <div className="space-y-3">
            <h3 className="text-lg font-bold text-foreground flex items-center gap-2">
              <Icon name="FileText" size={20} className="text-primary" />
              Original Booking
            </h3>
            <div className="p-4 bg-muted/50 rounded-lg border border-border space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Property</span>
                <span className="text-sm font-semibold text-foreground">{booking?.name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Confirmation #</span>
                <span className="text-sm font-semibold text-foreground">{booking?.confirmationNumber}</span>
              </div>
            </div>
          </div>

          {/* Modifications Summary */}
          <div className="space-y-3">
            <h3 className="text-lg font-bold text-foreground flex items-center gap-2">
              <Icon name="Edit" size={20} className="text-secondary" />
              Your Modifications
            </h3>
            <div className="space-y-3">
              {/* Date Changes */}
              {modifications?.dates && (
                <div className="p-4 bg-primary/5 rounded-lg border border-primary/20">
                  <div className="flex items-center gap-2 mb-3">
                    <Icon name="Calendar" size={16} className="text-primary" />
                    <span className="text-sm font-semibold text-foreground">Date Changes</span>
                  </div>
                  <div className="space-y-2 ml-6">
                    <div className="flex items-center gap-2 text-sm">
                      <span className="text-muted-foreground">Check-in:</span>
                      <span className="line-through text-muted-foreground">{formatDate(booking?.checkIn)}</span>
                      <Icon name="ArrowRight" size={14} className="text-primary" />
                      <span className="font-semibold text-success">{formatDate(modifications?.dates?.checkIn)}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <span className="text-muted-foreground">Check-out:</span>
                      <span className="line-through text-muted-foreground">{formatDate(booking?.checkOut)}</span>
                      <Icon name="ArrowRight" size={14} className="text-primary" />
                      <span className="font-semibold text-success">{formatDate(modifications?.dates?.checkOut)}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Accommodation Changes */}
              {modifications?.accommodation && (
                <div className="p-4 bg-secondary/5 rounded-lg border border-secondary/20">
                  <div className="flex items-center gap-2 mb-2">
                    <Icon name="Building" size={16} className="text-secondary" />
                    <span className="text-sm font-semibold text-foreground">Accommodation Change</span>
                  </div>
                  <div className="ml-6">
                    <p className="text-sm text-foreground">{modifications?.accommodation?.label}</p>
                  </div>
                </div>
              )}

              {/* Activity Changes */}
              {modifications?.activities?.added?.length > 0 && (
                <div className="p-4 bg-accent/5 rounded-lg border border-accent/20">
                  <div className="flex items-center gap-2 mb-2">
                    <Icon name="Compass" size={16} className="text-accent" />
                    <span className="text-sm font-semibold text-foreground">Added Activities</span>
                  </div>
                  <div className="ml-6 space-y-1">
                    {modifications?.activities?.added?.map((activity, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <Icon name="Plus" size={12} className="text-success" />
                        <span className="text-sm text-foreground">{activity?.label}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Pricing Summary */}
          <div className="space-y-3">
            <h3 className="text-lg font-bold text-foreground flex items-center gap-2">
              <Icon name="DollarSign" size={20} className="text-accent" />
              Pricing Summary
            </h3>
            <div className="p-4 bg-muted/50 rounded-lg border border-border space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Base Amount</span>
                <span className="text-sm font-medium text-foreground">
                  {formatCurrency(updatedPricing?.baseAmount)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Taxes & Fees</span>
                <span className="text-sm font-medium text-foreground">
                  {formatCurrency(updatedPricing?.taxesAndFees)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Service Fee</span>
                <span className="text-sm font-medium text-foreground">
                  {formatCurrency(updatedPricing?.serviceFee)}
                </span>
              </div>
              {changeFees > 0 && (
                <div className="flex justify-between">
                  <span className="text-sm text-warning">Modification Fees</span>
                  <span className="text-sm font-medium text-warning">
                    {formatCurrency(changeFees)}
                  </span>
                </div>
              )}
              <div className="pt-3 border-t border-border flex justify-between">
                <span className="text-base font-bold text-foreground">New Total</span>
                <span className="text-2xl font-bold text-primary">
                  {formatCurrency(updatedPricing?.total)}
                </span>
              </div>
            </div>
          </div>

          {/* Important Notice */}
          <div className="p-4 bg-warning/10 rounded-lg border border-warning/20">
            <div className="flex items-start gap-3">
              <Icon name="AlertTriangle" size={20} className="text-warning mt-0.5 flex-shrink-0" />
              <div className="space-y-2">
                <p className="text-sm font-semibold text-foreground">Important Notice</p>
                <ul className="text-xs text-muted-foreground space-y-1">
                  <li>• By confirming, you agree to the new booking terms and pricing</li>
                  <li>• A new confirmation number will be issued</li>
                  <li>• Modification fees are non-refundable</li>
                  <li>• Changes are subject to final availability confirmation</li>
                  <li>• You will receive a confirmation email with updated details</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 pt-4">
            <Button
              variant="outline"
              onClick={onClose}
              className="flex-1"
            >
              <Icon name="X" size={18} />
              Cancel
            </Button>
            <Button
              onClick={onConfirm}
              className="flex-1 bg-primary hover:bg-primary/90"
            >
              <Icon name="Check" size={18} />
              Confirm Modifications
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationModal;